/**
 * Phase 6: Energy guidance (permission + preservation).
 * Invokes energy-guidance edge function; contract: docs/contracts/PHASE6_ENERGY_GUIDANCE_API.md
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getFunctionsErrorBody, formatFunctionsError, isNoTelemetryError } from '@/lib/functions-error';

export type EnergyGuidanceState =
  | 'permission_early'
  | 'permission_spill'
  | 'neutral'
  | 'preservation';

export interface EnergyGuidanceResult {
  propertyId: string;
  state: EnergyGuidanceState;
  confidence: 'low' | 'medium' | 'high';
  message: string;
  /** Optional explainability: e.g. "Expected to reach full charge before sunset." */
  explanation: string | null;
  forecast_lower_kwh: number | null;
  reserve_kwh: number | null;
  spill_kwh_estimate: number | null;
  reason: string | null;
  decided_at: string;
}

/** Optional forecast override to align backend guidance with dashboard display. */
export interface GuidanceForecastOverride {
  expectedAtSunriseKwh: number;
  worstAtSunriseKwh: number;
  reserveThresholdKwh: number;
  forecastSource: 'forecast-runway' | 'overnight_forecasts';
}

export function useEnergyGuidance(propertyId: string | undefined, forecastOverride?: GuidanceForecastOverride | null) {
  return useQuery({
    queryKey: ['energy-guidance', propertyId, forecastOverride?.worstAtSunriseKwh, forecastOverride?.forecastSource],
    queryFn: async (): Promise<EnergyGuidanceResult | null> => {
      if (!propertyId) return null;
      // [perf] DIAG: energy-guidance timing (remove after diagnosis)
      const t0 = performance.now();
      const body: Record<string, unknown> = { propertyId };
      if (forecastOverride && Number.isFinite(forecastOverride.worstAtSunriseKwh)) {
        body.forecastOverride = forecastOverride;
      }
      const { data, error } = await supabase.functions.invoke<{
        success: boolean;
        propertyId?: string;
        state?: EnergyGuidanceState;
        confidence?: 'low' | 'medium' | 'high';
        message?: string;
        explanation?: string | null;
        forecast_lower_kwh?: number | null;
        reserve_kwh?: number | null;
        spill_kwh_estimate?: number | null;
        reason?: string | null;
        decided_at?: string;
      }>('energy-guidance', { method: 'POST', body });
      const duration = Math.round(performance.now() - t0);
      if (typeof console !== 'undefined' && console.log) {
        console.log(`[perf] energy-guidance: ${duration}ms`);
      }
      if (error) {
        const backend = await getFunctionsErrorBody(error);
        if (backend && isNoTelemetryError(backend)) {
          if (typeof console !== 'undefined' && console.log) {
            console.log('[energy-guidance] No plant telemetry available for energy guidance');
          }
          return null;
        }
        if (backend) console.warn('[energy-guidance]', formatFunctionsError(backend));
        throw error;
      }
      if (data?.success && data?.state != null) {
        return {
          propertyId: data.propertyId ?? propertyId,
          state: data.state,
          confidence: data.confidence ?? 'medium',
          message: data.message ?? '',
          explanation: data.explanation ?? null,
          forecast_lower_kwh: data.forecast_lower_kwh ?? null,
          reserve_kwh: data.reserve_kwh ?? null,
          spill_kwh_estimate: data.spill_kwh_estimate ?? null,
          reason: data.reason ?? null,
          decided_at: data.decided_at ?? new Date().toISOString(),
        };
      }
      return null;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
  });
}
